import React, { useEffect } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { usePathname } from 'expo-router';
import { DockedPanel } from '../layout/DockedPanel';
import { PanelHeader } from '../layout/PanelHeader';
import { PanelStack } from '../layout/PanelStack';
import { SPACING } from '../../theme';
import { DETAIL_PANEL_WIDTH } from '../../theme/layout';
import { useUIStore } from '../../store/uiStore';
import { useStructuresStore } from '../../store/structuresStore';
import { useEscapeToClose } from '../../utils/escapeStack';
import { useLeavingPanel } from '../../hooks/useLeavingPanel';
import { isMapRoute } from '../../utils/sidebarVariant';
import { AddPropertyForm } from './AddPropertyForm';
import { PropertyPickerBody } from './PropertyPickerBody';

/** nativeID for the pinned header's title — wired to the DockedPanel's
 *  ariaLabelledBy, same pattern as the post panel's POST_PANEL_HEADING_ID. */
const ADD_PROPERTY_PANEL_HEADING_ID = 'add-property-panel-heading';

/**
 * Desktop map route host for AddPropertyForm/PropertyPickerBody: a
 * right-docked panel (testID `add-property-panel`) instead of the two
 * global AdaptiveSheet dialogs those forms use everywhere else
 * (panel-system.md Phase 2, "AddProperty and PropertyPicker become
 * right-dock views"). Mounted by app/(tabs)/index.tsx only on the desktop
 * shell while `activeSheet` is `addProperty` or `propertyPicker` —
 * app/_layout.tsx suppresses the two AdaptiveSheet hosts under that same
 * condition so only one presentation of the flow is ever on screen. Add and
 * Picker are today two independent entry points rather than a chained flow
 * (see PropertyPickerSheet's own callers), so this swaps the PanelStack's
 * `root` between them rather than pushing one from the other — the stack is
 * still here so a future add-to-pick handoff is a plain `push()`.
 *
 * Same right-hand dock surface as the structure and post panels (see
 * app/(tabs)/index.tsx's handleStructurePress/handlePostPress and
 * Map.web.tsx's handleOpenSlideshow): opening this clears those, and
 * selecting a structure or a post closes this (both call `closeSheet()`).
 */
export function PropertyDockPanel() {
  const activeSheet = useUIStore((s) => s.activeSheet);
  const addPropertyInitialQuery = useUIStore((s) => s.addPropertyInitialQuery);
  const closeSheet = useUIStore((s) => s.closeSheet);
  const isAddProperty = activeSheet === 'addProperty';
  const isPicker = activeSheet === 'propertyPicker';
  // The map tab can stay mounted under other routes, where app/_layout.tsx
  // shows the AdaptiveSheet hosts instead; gating on the route here keeps
  // the flow to one presentation at a time.
  const onMap = isMapRoute(usePathname());
  const open = onMap && (isAddProperty || isPicker);
  // Another right-dock surface taking the slot in this same render replaces
  // this panel at once instead of sliding out past it (see the structure and
  // post panels in app/(tabs)/index.tsx).
  const otherRightDock = useStructuresStore((s) => s.selectedStructure != null);
  const postDocked = useUIStore((s) => s.dockedPostId != null);
  const composerOpen = useUIStore((s) => s.composerDockOpen);
  const presenting = useUIStore((s) => s.presentationActive);
  // The title and body come from the last open render so they don't flip to
  // the empty state while the panel slides out.
  const leave = useLeavingPanel(
    open,
    { isAddProperty, initialQuery: addPropertyInitialQuery },
    { instant: otherRightDock || postDocked || composerOpen || presenting }
  );
  // The AdaptiveSheet host closed on Escape; the dock has to register it
  // itself.
  useEscapeToClose(open, closeSheet);

  // Opening this dock clears the other right-hand surfaces — same
  // "opening one clears the others" pattern as handleStructurePress and
  // handlePostPress. Only fires on the open transition (the deps below),
  // not on every render while open.
  useEffect(() => {
    if (!open) return;
    useStructuresStore.getState().selectStructure(null);
    useUIStore.getState().closeDockedPost();
    useUIStore.getState().requestSlideshowClose();
    useUIStore.getState().closeComposerDock();
  }, [open]);

  if (!leave.mounted || !leave.lastContent) return null;

  const showAddProperty = leave.lastContent.isAddProperty;
  const title = showAddProperty ? 'Add a property' : 'Your properties';

  return (
    <DockedPanel
      testID="add-property-panel"
      width={DETAIL_PANEL_WIDTH}
      scroll={false}
      shown={leave.shown}
      onExited={leave.onExited}
      ariaLabelledBy={ADD_PROPERTY_PANEL_HEADING_ID}>
      {/* Pinned title + close (panel-system.md review, note 11 — this panel
          had no close button at all before, only Escape) with a scrolling
          body underneath, same DockedPanel scroll={false} split as the post
          panel (note 4). */}
      <View style={styles.container}>
        <PanelHeader
          title={title}
          onClose={closeSheet}
          headingId={ADD_PROPERTY_PANEL_HEADING_ID}
          testID="add-property-panel-close"
        />
        <ScrollView
          style={styles.scrollBody}
          contentContainerStyle={styles.scrollBodyContent}
          showsVerticalScrollIndicator={false}>
          <PanelStack
            root={
              showAddProperty ? (
                <AddPropertyForm
                  visible
                  onClose={closeSheet}
                  initialAddress={leave.lastContent.initialQuery}
                  hideTitle
                />
              ) : (
                <PropertyPickerBody visible onClose={closeSheet} hideTitle />
              )
            }
          />
        </ScrollView>
      </View>
    </DockedPanel>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  scrollBody: {
    flex: 1,
  },
  scrollBodyContent: {
    paddingBottom: SPACING.lg,
  },
});
